import { useEffect, useRef } from "react";

import type { MessageDto } from "../api/types.js";
import { ProductCards } from "./ProductCards.js";

/**
 * The conversation, oldest first.
 *
 * Message text is rendered as **text**, never as markup. React escapes it by
 * default, and `white-space: pre-wrap` preserves the newlines the platform stored —
 * so a customer pasting HTML sees their HTML, and an agent's browser does not run it.
 */

function bubbleClass(message: MessageDto): string {
  const classes = [
    "bubble",
    `from-${message.senderType === "guest" ? "customer" : message.senderType}`,
  ];

  if (message.deleted) classes.push("deleted");
  // A lone emoji renders large, as it does everywhere else. The platform classified
  // it at send time, so the client does not have to guess.
  if (message.messageType === "emoji") classes.push("emoji-only");

  return classes.join(" ");
}

/**
 * Whose bubble this is.
 *
 * `requesterName` is the fallback for the person the conversation belongs to, and
 * it matters more than a fallback usually would. A message carries a display name
 * only if one was known when it was sent, so every message sent before the platform
 * started capturing names — and every message in a conversation that predates it —
 * would otherwise be labelled with the bare word "Customer", beside a queue row
 * showing the person's actual name. The conversation knows who they are even when
 * the message does not.
 */
function senderLabel(message: MessageDto, requesterName: string): string {
  if (message.senderDisplayName !== undefined && message.senderDisplayName !== "") {
    return message.senderDisplayName;
  }

  switch (message.senderType) {
    case "ai":
      return "Assistant";
    case "agent":
      return "Agent";
    case "system":
      return "";
    case "customer":
    case "guest":
      return requesterName;
  }
}

function time(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/**
 * Where an attachment's bytes live.
 *
 * Built from the id rather than sent by the server: the DTO deliberately
 * carries no URL and no storage key, because publishing a path would let
 * anyone who has seen one message guess where other people's files sit. The
 * download route re-checks that this caller may read the conversation.
 *
 * `audience` rides in the query string because a browser cannot put a header
 * on an `<img src>`, and a support agent signed into the shop as well holds two
 * sessions — without it the realtime service would have to guess which one the
 * image belongs to.
 */
function attachmentUrl(attachmentId: string): string {
  const base = import.meta.env.VITE_API_BASE_URL ?? "";

  return `${base}/attachments/${encodeURIComponent(attachmentId)}?audience=staff`;
}

export function Transcript({
  onViewImage,
  messages,
  viewerId,
  requesterName = "Customer",
}: {
  readonly messages: readonly MessageDto[];
  readonly viewerId: string | null;
  /** What to call the customer on a message that carries no name of its own. */
  readonly requesterName?: string;
  /** Opens an attachment in the shell's image dialog. */
  readonly onViewImage?: (src: string, alt: string) => void;
}) {
  const endRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  /**
   * Follow new messages, but only when already at the bottom.
   *
   * Yanking an agent to the newest message while they are reading back through
   * history is one of the more irritating things a chat client can do.
   */
  useEffect(() => {
    const container = containerRef.current;
    if (container === null) return;

    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;

    if (distanceFromBottom < 150) {
      endRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  return (
    <div className="messages" ref={containerRef} role="log" aria-label="Conversation transcript">
      {messages.length === 0 && <p className="muted small">No messages yet.</p>}

      {messages.map((message) => (
        <div key={message.messageId} className={bubbleClass(message)}>
          {message.text}

          <ProductCards products={message.products} />

          {message.attachments.length > 0 && (
            <div className="attachments">
              {message.attachments.map((attachment) =>
                attachment.kind === "image" ? (
                  /*
                   * Shown, not listed.
                   *
                   * A customer sending a photo is usually sending the whole
                   * question — a damaged parcel, a label, a screenshot of an
                   * error — and a filename with a size beside it made the agent
                   * download it to find out what they were being asked about.
                   * Opens full size in a new tab.
                   */
                  /*
                    Opened in a dialog rather than a new tab. A new tab loses the
                    conversation the agent is answering, which is the thing they
                    were looking at the picture for.
                  */
                  <button
                    key={attachment.attachmentId}
                    type="button"
                    className="attachment-open"
                    title={attachment.fileName}
                    onClick={() => {
                      onViewImage?.(attachmentUrl(attachment.attachmentId), attachment.fileName);
                    }}
                  >
                    <img
                      className="attachment-image"
                      src={attachmentUrl(attachment.attachmentId)}
                      alt={attachment.fileName}
                      loading="lazy"
                      /* A broken-file icon tells an agent nothing they can act
                         on; a line of text at least names the problem. */
                      onError={(event) => {
                        const image = event.currentTarget;
                        image.style.display = "none";
                        image.insertAdjacentHTML(
                          "afterend",
                          '<span class="small muted">📎 Image unavailable</span>',
                        );
                      }}
                    />
                  </button>
                ) : (
                  <a
                    key={attachment.attachmentId}
                    className="small"
                    href={attachmentUrl(attachment.attachmentId)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {attachment.fileName}{" "}
                    <span className="muted">({Math.ceil(attachment.sizeBytes / 1024)} KB)</span>
                  </a>
                ),
              )}
            </div>
          )}

          {message.reactions.length > 0 && (
            <div className="row" style={{ marginTop: 6 }}>
              {message.reactions.map((reaction) => (
                <span key={reaction.emoji} className="tag">
                  {reaction.emoji} {reaction.count}
                </span>
              ))}
            </div>
          )}

          <div className="meta">
            {senderLabel(message, requesterName)} · {time(message.createdAt)}
            {message.edited && " · edited"}
            {/* Whether the customer has seen it. `viewerId` is excluded: an agent
                seeing their own message is not information. */}
            {message.readBy.some((read) => read.actorId !== viewerId) && " · seen"}
          </div>
        </div>
      ))}

      <div ref={endRef} />
    </div>
  );
}
